import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { AdminCampaignReseller, getAdminCampaignResellers } from '../../../services/admin-service';

const AdminCampaignResellersPage: React.FC = () => {
  const { template_id } = useParams<{ template_id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setBreadcrumbs } = useBreadcrumb();

  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [resellers, setResellers] = useState<AdminCampaignReseller[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Painel', path: '/admin' },
      { label: 'Campanhas', path: '/admin/campaigns' },
      {
        label: 'Revendas',
        path: template_id ? `/admin/campaigns/${template_id}/resellers` : '/admin/campaigns',
      },
    ]);
  }, [setBreadcrumbs, template_id]);

  useEffect(() => {
    const fetchCampaignResellers = async () => {
      if (!template_id) {
        enqueueSnackbar('Campanha inválida', { variant: 'error' });
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const withActivities = activeTab === 0;
        const response = await getAdminCampaignResellers(
          template_id,
          page * rowsPerPage,
          rowsPerPage,
          withActivities
        );

        if (response) {
          setTitle(response.title);
          setDescription(response.description);
          setTargetAudience(response.target_audience);
          setResellers(response.items);
          setTotalItems(response.total);
        } else {
          enqueueSnackbar('Falha ao carregar revendas da campanha', { variant: 'error' });
          setResellers([]);
          setTotalItems(0);
        }
      } catch (error) {
        console.error('Error fetching campaign resellers:', error);
        enqueueSnackbar('Falha ao carregar revendas da campanha', { variant: 'error' });
        setResellers([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignResellers();
  }, [template_id, page, rowsPerPage, activeTab, enqueueSnackbar]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/campaigns')}>
          Voltar para Campanhas
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2">Título</Typography>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          {title || '-'}
        </Typography>

        <Typography variant="subtitle2">Descrição</Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1.5 }}>
          {description || '-'}
        </Typography>

        <Typography variant="subtitle2">Público Alvo</Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {targetAudience || '-'}
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={handleChangeTab}>
          <Tab label="Revendas ativas na campanha" />
          <Tab label="Revendas sem atividades na campanha" />
        </Tabs>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Revenda</TableCell>
                  <TableCell>Email</TableCell>
                  {activeTab === 0 ? (
                    <>
                      <TableCell align="center">Chats</TableCell>
                      <TableCell align="center">Leads</TableCell>
                      <TableCell align="center">Solicitações de Orçamento</TableCell>
                    </>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {resellers.length > 0 ? (
                  resellers.map(reseller => (
                    <TableRow
                      key={reseller.id}
                      hover
                      onClick={() => navigate(`/admin/campaigns/${reseller.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{reseller.reseller_name}</TableCell>
                      <TableCell>{reseller.reseller_email}</TableCell>
                      {activeTab === 0 ? (
                        <>
                          <TableCell align="center">{reseller.started_chats}</TableCell>
                          <TableCell align="center">{reseller.leads_count}</TableCell>
                          <TableCell align="center">{reseller.requested_quotes}</TableCell>
                        </>
                      ) : null}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={activeTab === 0 ? 5 : 2} align="center">
                      Nenhuma revenda encontrada para esta campanha
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </>
      )}
    </Box>
  );
};

export default AdminCampaignResellersPage;
